import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { negotiationAPI } from "@/lib/api";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { 
  Loader2,
  IndianRupee,
  ArrowRight,
  Check,
  X,
  MessageSquare,
  Clock,
  AlertCircle,
  User,
  Building2
} from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  countered: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-800"
};

/**
 * Dialog for viewing and responding to a negotiation
 */
export function NegotiationDetailDialog({ 
  open, 
  onOpenChange, 
  negotiation,
  currentUserId,
  onUpdate 
}) {
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMessage, setCounterMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [action, setAction] = useState(null); // "counter", "accept", "reject"

  if (!negotiation) return null;

  const isUser = negotiation.user_id === currentUserId;
  const isPartner = negotiation.partner_id === currentUserId;
  const isMyTurn = (isUser && negotiation.whose_turn === "user") || 
                   (isPartner && negotiation.whose_turn === "partner");
  const isActive = ["pending", "countered"].includes(negotiation.status);
  const canRespond = isActive && isMyTurn;

  const handleCounter = async () => {
    const price = parseFloat(counterPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    setSubmitting(true);
    try {
      await negotiationAPI.counter(negotiation.id, {
        counter_price: price,
        message: counterMessage || undefined
      });
      toast.success("Counter offer sent!");
      setCounterPrice("");
      setCounterMessage("");
      setAction(null);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send counter offer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await negotiationAPI.accept(negotiation.id);
      toast.success("Offer accepted! You can now proceed with booking.");
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept offer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await negotiationAPI.reject(negotiation.id);
      toast.success("Negotiation declined");
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Price Negotiation</span>
            <span className={`px-2 py-1 text-xs rounded ${statusColors[negotiation.status]}`}>
              {negotiation.status.charAt(0).toUpperCase() + negotiation.status.slice(1)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Listing Info */}
          <div className="bg-muted/50 p-3 border border-border">
            <p className="font-medium">{negotiation.listing_title}</p>
            <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
              <span>Original: ₹{negotiation.original_price} {negotiation.price_unit?.replace("_", "/")}</span>
              {negotiation.space_required && <span>Space: {negotiation.space_required} kg</span>}
              {negotiation.seats_required && <span>Seats: {negotiation.seats_required}</span>}
            </div>
          </div>

          {/* Participants */}
          <div className="flex justify-between text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className={isUser ? "font-bold" : ""}>{negotiation.user_name}</span>
              {isUser && <span className="text-xs text-muted-foreground">(You)</span>}
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className={isPartner ? "font-bold" : ""}>{negotiation.partner_name}</span>
              {isPartner && <span className="text-xs text-muted-foreground">(You)</span>}
            </div>
          </div>

          {/* Offer History */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Negotiation History</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {negotiation.offers?.map((offer, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 border ${
                    offer.from_type === "user" ? "border-l-4 border-l-blue-500" : "border-l-4 border-l-orange-500"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-medium text-sm">
                        {offer.from_type === "user" ? negotiation.user_name : negotiation.partner_name}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({offer.from_type === "user" ? "User" : "Partner"})
                      </span>
                    </div>
                    <span className="text-lg font-bold text-orange-600">₹{offer.price}</span>
                  </div>
                  {offer.message && (
                    <p className="text-sm text-muted-foreground mt-1">"{offer.message}"</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(offer.timestamp), { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-muted/30 p-3 border border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm">Current Offer:</span>
              <span className="text-2xl font-black text-orange-600">₹{negotiation.current_offer}</span>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>Round {negotiation.current_round} of {negotiation.max_rounds}</span>
              {isActive && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Expires {formatDistanceToNow(new Date(negotiation.expires_at), { addSuffix: true })}
                </span>
              )}
            </div>
          </div>

          {/* Response Actions */}
          {canRespond && (
            <div className="space-y-3 pt-2">
              {action === "counter" ? (
                <div className="space-y-3 border border-border p-3">
                  <div className="space-y-2">
                    <Label>Your Counter Offer</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        placeholder="Enter your price"
                        value={counterPrice}
                        onChange={(e) => setCounterPrice(e.target.value)}
                        className="pl-8 rounded-none"
                        data-testid="counter-price"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Message (Optional)</Label>
                    <Textarea
                      placeholder="Add a message..."
                      value={counterMessage}
                      onChange={(e) => setCounterMessage(e.target.value)}
                      className="rounded-none resize-none"
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-none"
                      onClick={() => setAction(null)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1 rounded-none bg-blue-600 hover:bg-blue-700"
                      onClick={handleCounter}
                      disabled={submitting || !counterPrice}
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Send Counter
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    className="rounded-none bg-green-600 hover:bg-green-700"
                    onClick={handleAccept}
                    disabled={submitting}
                    data-testid="accept-offer-btn"
                  >
                    {submitting && action === "accept" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Accept
                      </>
                    )}
                  </Button>
                  {negotiation.current_round < negotiation.max_rounds && (
                    <Button
                      variant="outline"
                      className="rounded-none"
                      onClick={() => setAction("counter")}
                      disabled={submitting}
                      data-testid="counter-offer-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Counter
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    className="rounded-none"
                    onClick={handleReject}
                    disabled={submitting}
                    data-testid="reject-offer-btn"
                  >
                    {submitting && action === "reject" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <X className="w-4 h-4 mr-1" />
                        Decline
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Waiting Message */}
          {isActive && !isMyTurn && (
            <div className="bg-amber-50 border border-amber-200 p-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                Waiting for {negotiation.whose_turn === "user" ? negotiation.user_name : negotiation.partner_name} to respond
              </span>
            </div>
          )}

          {/* Accepted - Show Book Now */}
          {negotiation.status === "accepted" && (
            <div className="bg-green-50 border border-green-200 p-4 text-center">
              <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium text-green-800">Offer Accepted!</p>
              <p className="text-sm text-green-700 mt-1">
                You can now book at ₹{negotiation.current_offer} {negotiation.price_unit?.replace("_", "/")}
              </p>
              <Button 
                className="mt-3 rounded-none bg-green-600 hover:bg-green-700"
                onClick={() => {
                  onOpenChange(false);
                  // Navigate to listing with negotiated price
                  window.location.href = `/${negotiation.listing_type === "cargo" ? "cargo" : "jets"}/${negotiation.listing_id}?negotiated=${negotiation.id}`;
                }}
              >
                Proceed to Book
              </Button>
            </div>
          )}

          {/* Max Rounds Reached */}
          {negotiation.current_round >= negotiation.max_rounds && isActive && (
            <div className="bg-amber-50 border border-amber-200 p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                Maximum negotiation rounds reached. Accept or decline to conclude.
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
